import { For, Show, createMemo, createSignal, onMount } from "solid-js";
import { api, type MaintenanceTaskDescriptor, type MaintenanceTaskLatestSummary, type MaintenanceTaskResult, type MaintenanceTaskRunListResponse, type Organization, type Project } from "../shared";

function formatOptionalDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "-";
}

export function MaintenancePanel() {
  const [tasks, setTasks] = createSignal<MaintenanceTaskDescriptor[]>([]);
  const [organizations, setOrganizations] = createSignal<Organization[]>([]);
  const [projects, setProjects] = createSignal<Project[]>([]);
  const [latestRuns, setLatestRuns] = createSignal<MaintenanceTaskLatestSummary[]>([]);
  const [runsResponse, setRunsResponse] = createSignal<MaintenanceTaskRunListResponse>({
    items: [],
    page: 1,
    perPage: 25,
    total: 0,
    totalPages: 1,
  });
  const [selectedTaskId, setSelectedTaskId] = createSignal<string>("stale_active_sessions");
  const [selectedOrganizationId, setSelectedOrganizationId] = createSignal("");
  const [selectedProjectId, setSelectedProjectId] = createSignal("");
  const [previewLimit, setPreviewLimit] = createSignal(100);
  const [selectedStatuses, setSelectedStatuses] = createSignal<string[]>([
    "disconnected",
    "completed",
    "expired",
  ]);
  const [minAgeValue, setMinAgeValue] = createSignal(1);
  const [minAgeUnit, setMinAgeUnit] = createSignal<"seconds" | "minutes" | "hours" | "days">("days");
  const [minSamples, setMinSamples] = createSignal(1);
  const [maxPoints, setMaxPoints] = createSignal(0);
  const [runBatchLimit, setRunBatchLimit] = createSignal(100);
  const [staleSessionSourceStatuses, setStaleSessionSourceStatuses] =
    createSignal<Array<"paused" | "disconnected">>(["paused"]);
  const [pausedTargetStatus, setPausedTargetStatus] =
    createSignal<"completed" | "disconnected" | "expired">("completed");
  const [coverageExecutionMode, setCoverageExecutionMode] =
    createSignal<"queued" | "direct">("queued");
  const [forceCoverageRebuild, setForceCoverageRebuild] = createSignal(false);
  const [coverageBlocksPerBatch, setCoverageBlocksPerBatch] = createSignal(8);
  const [coverageCellsPerBatch, setCoverageCellsPerBatch] = createSignal(25_000);
  const [coverageCandidateSessionsPerBatch, setCoverageCandidateSessionsPerBatch] = createSignal(500);
  const [coverageTaskTimeBudgetMs, setCoverageTaskTimeBudgetMs] = createSignal(90_000);
  const [coverageMinimumSessionAgeValue, setCoverageMinimumSessionAgeValue] = createSignal(1);
  const [coverageMinimumSessionAgeUnit, setCoverageMinimumSessionAgeUnit] =
    createSignal<"minutes" | "hours" | "days">("hours");
  const [coverageIncludeActiveSessions, setCoverageIncludeActiveSessions] = createSignal(false);
  const [queueHistoryRecordTypes, setQueueHistoryRecordTypes] = createSignal<Array<"logical_executions" | "platform_wakeups">>(["logical_executions", "platform_wakeups"]);
  const [queueHistoryStatuses, setQueueHistoryStatuses] = createSignal<Array<"completed" | "failed" | "interrupted" | "skipped_overlap">>(["completed"]);
  const [queueHistoryEmptyOnly, setQueueHistoryEmptyOnly] = createSignal(true);
  const [historyTaskId, setHistoryTaskId] = createSignal("");
  const [historyStatus, setHistoryStatus] = createSignal<"" | "completed" | "failed">("");
  const [historyPage, setHistoryPage] = createSignal(1);
  const [historyPerPage, setHistoryPerPage] = createSignal(25);
  const [result, setResult] = createSignal<MaintenanceTaskResult | null>(null);
  const [loading, setLoading] = createSignal<"preview" | "run" | null>(null);
  const [runsLoading, setRunsLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  const [message, setMessage] = createSignal("");
  const [taskDropdownOpen, setTaskDropdownOpen] = createSignal(false);

  async function loadRuns() {
    setRunsLoading(true);
    try {
      const [latest, response] = await Promise.all([
        api.maintenance.latestRuns(),
        api.maintenance.runs({
          taskId: historyTaskId() || undefined,
          status: historyStatus() || undefined,
          page: historyPage(),
          perPage: historyPerPage(),
        }),
      ]);
      setLatestRuns(latest);
      setRunsResponse(response);
      if (response.page !== historyPage()) {
        setHistoryPage(response.page);
      }
    } finally {
      setRunsLoading(false);
    }
  }

  onMount(async () => {
    try {
      const [taskRows, orgRows, projectRows] = await Promise.all([
        api.maintenance.tasks(),
        api.organizations.list(),
        api.projects.list(),
      ]);
      setTasks(taskRows);
      setOrganizations(orgRows);
      setProjects(projectRows);
      await loadRuns();
    } catch (e: any) {
      setError(e.message ?? "Failed to load maintenance data");
    }
  });

  const selectedTask = () => tasks().find((task) => task.id === selectedTaskId()) ?? null;
  const filteredProjects = () => {
    const organizationId = selectedOrganizationId();
    if (!organizationId) return projects();
    return projects().filter((project) => project.organizationId === organizationId);
  };

  const isEmptySessionArchiveTask = () => selectedTaskId() === "archive_empty_non_active_sessions";
  const isStaleSessionTask = () => selectedTaskId() === "transition_stale_paused_sessions";
  const isSessionPathMaterializationTask = () => selectedTaskId() === "repair_session_path_materializations";
  const isProjectBlockCoverageTask = () => selectedTaskId() === "materialize_project_block_coverage";
  const isQueueHistoryCleanupTask = () => selectedTaskId() === "cleanup_task_queue_execution_history";
  const showsSourceStatusFilters = () => isEmptySessionArchiveTask() || isSessionPathMaterializationTask();
  const showsAgeFilters = () => showsSourceStatusFilters() || isStaleSessionTask() || isQueueHistoryCleanupTask();

  function applyTaskDefaults(taskId: string) {
    if (taskId === "archive_empty_non_active_sessions") {
      setSelectedStatuses(["disconnected", "completed", "expired"]);
      setMinAgeValue(1);
      setMinAgeUnit("days");
      setMinSamples(1);
      setMaxPoints(0);
      setRunBatchLimit(100);
      return;
    }
    if (taskId === "transition_stale_paused_sessions") {
      setStaleSessionSourceStatuses(["paused"]);
      setMinAgeValue(24);
      setMinAgeUnit("hours");
      setPausedTargetStatus("completed");
      setRunBatchLimit(500);
      return;
    }
    if (taskId === "repair_session_path_materializations") {
      setSelectedStatuses(["paused", "disconnected", "completed", "expired"]);
      setMinAgeValue(1);
      setMinAgeUnit("days");
      setMinSamples(1);
      setRunBatchLimit(100);
      return;
    }
    if (taskId === "materialize_project_block_coverage") {
      setCoverageExecutionMode("queued");
      setForceCoverageRebuild(false);
      setCoverageBlocksPerBatch(8);
      setCoverageCellsPerBatch(25_000);
      setCoverageCandidateSessionsPerBatch(500);
      setCoverageTaskTimeBudgetMs(90_000);
      setCoverageMinimumSessionAgeValue(1);
      setCoverageMinimumSessionAgeUnit("hours");
      setCoverageIncludeActiveSessions(false);
      return;
    }
    if (taskId === "cleanup_task_queue_execution_history") {
      setQueueHistoryRecordTypes(["logical_executions", "platform_wakeups"]);
      setQueueHistoryStatuses(["completed"]);
      setQueueHistoryEmptyOnly(true);
      setMinAgeValue(1);
      setMinAgeUnit("days");
      setRunBatchLimit(500);
    }
  }

  const currentScope = () => ({
    organizationId: selectedOrganizationId() || null,
    projectId: selectedProjectId() || null,
    limit: previewLimit(),
    runLimit: isSessionPathMaterializationTask() || isStaleSessionTask() || isQueueHistoryCleanupTask()
      ? runBatchLimit()
      : undefined,
    statuses: showsSourceStatusFilters() ? selectedStatuses() : undefined,
    minAgeValue: showsAgeFilters() ? minAgeValue() : undefined,
    minAgeUnit: showsAgeFilters() ? minAgeUnit() : undefined,
    minSamples: isSessionPathMaterializationTask() ? minSamples() : undefined,
    maxPoints: isEmptySessionArchiveTask() ? maxPoints() : undefined,
    staleSessionSourceStatuses: isStaleSessionTask()
      ? staleSessionSourceStatuses()
      : undefined,
    pausedTargetStatus: isStaleSessionTask() ? pausedTargetStatus() : undefined,
    coverageExecutionMode: isProjectBlockCoverageTask()
      ? coverageExecutionMode()
      : undefined,
    forceCoverageRebuild: isProjectBlockCoverageTask()
      ? forceCoverageRebuild()
      : undefined,
    coverageBlocksPerBatch: isProjectBlockCoverageTask() ? coverageBlocksPerBatch() : undefined,
    coverageCellsPerBatch: isProjectBlockCoverageTask() ? coverageCellsPerBatch() : undefined,
    coverageCandidateSessionsPerBatch: isProjectBlockCoverageTask()
      ? coverageCandidateSessionsPerBatch()
      : undefined,
    coverageTaskTimeBudgetMs: isProjectBlockCoverageTask() ? coverageTaskTimeBudgetMs() : undefined,
    coverageMinimumSessionAgeValue: isProjectBlockCoverageTask()
      ? coverageMinimumSessionAgeValue()
      : undefined,
    coverageMinimumSessionAgeUnit: isProjectBlockCoverageTask()
      ? coverageMinimumSessionAgeUnit()
      : undefined,
    coverageIncludeActiveSessions: isProjectBlockCoverageTask()
      ? coverageIncludeActiveSessions()
      : undefined,
    queueHistoryRecordTypes: isQueueHistoryCleanupTask() ? queueHistoryRecordTypes() : undefined,
    queueHistoryStatuses: isQueueHistoryCleanupTask() ? queueHistoryStatuses() : undefined,
    queueHistoryEmptyOnly: isQueueHistoryCleanupTask() ? queueHistoryEmptyOnly() : undefined,
  });
  const runs = () => runsResponse().items;
  const visiblePages = createMemo(() => {
    const totalPages = runsResponse().totalPages;
    const currentPage = runsResponse().page;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    const normalizedStart = Math.max(1, end - 4);
    return Array.from({ length: end - normalizedStart + 1 }, (_, index) => normalizedStart + index);
  });

  function updateHistoryFilters(next: {
    taskId?: string;
    status?: "" | "completed" | "failed";
    perPage?: number;
    page?: number;
  }) {
    if (next.taskId !== undefined) setHistoryTaskId(next.taskId);
    if (next.status !== undefined) setHistoryStatus(next.status);
    if (next.perPage !== undefined) setHistoryPerPage(next.perPage);
    setHistoryPage(next.page ?? 1);
    void loadRuns();
  }

  function goToHistoryPage(page: number) {
    setHistoryPage(page);
    void loadRuns();
  }

  function toggleStatus(status: string) {
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status],
    );
  }

  function toggleStaleSessionSourceStatus(status: "paused" | "disconnected") {
    setStaleSessionSourceStatuses((current) => {
      if (!current.includes(status)) return [...current, status];
      if (current.length === 1) return current;
      return current.filter((value) => value !== status);
    });
  }

  function toggleQueueHistoryRecordType(recordType: "logical_executions" | "platform_wakeups") {
    setQueueHistoryRecordTypes((current) => current.includes(recordType)
      ? current.length === 1 ? current : current.filter((value) => value !== recordType)
      : [...current, recordType]);
  }

  function toggleQueueHistoryStatus(status: "completed" | "failed" | "interrupted" | "skipped_overlap") {
    setQueueHistoryStatuses((current) => current.includes(status)
      ? current.length === 1 ? current : current.filter((value) => value !== status)
      : [...current, status]);
  }

  async function runPreview() {
    if (isProjectBlockCoverageTask() && coverageExecutionMode() === "direct" && !selectedProjectId()) {
      setError("Direct block coverage execution requires one selected project.");
      return;
    }
    setLoading("preview");
    setError("");
    setMessage("");
    try {
      setResult(await api.maintenance.preview(selectedTaskId(), currentScope()));
    } catch (e: any) {
      setError(e.message ?? "Failed to preview maintenance task");
    } finally {
      setLoading(null);
    }
  }

  async function runTask() {
    const task = selectedTask();
    if (!task) return;
    if (isProjectBlockCoverageTask() && coverageExecutionMode() === "direct" && !selectedProjectId()) {
      setError("Direct block coverage execution requires one selected project.");
      return;
    }
    const coverageScope =
      selectedProjectId()
        ? "the selected project"
        : selectedOrganizationId()
          ? "all eligible projects in the selected organization"
          : "all eligible projects across all organizations";
    const confirmed = window.confirm(
      isProjectBlockCoverageTask()
        ? `Schedule "${task.label}" for ${coverageScope}? Coverage work will run in durable batches.`
        : isStaleSessionTask()
          ? `Transition up to ${runBatchLimit()} stale ${staleSessionSourceStatuses().join(" and ")} session(s) to ${pausedTargetStatus()}?`
        : isQueueHistoryCleanupTask()
          ? `Delete up to ${runBatchLimit()} matching task queue history record(s)? Preview first to verify the selected categories, statuses, and age threshold.`
          : `Run maintenance task "${task.label}" now? This will apply database changes.`,
    );
    if (!confirmed) return;
    setLoading("run");
    setError("");
    setMessage("");
    try {
      const taskResult = await api.maintenance.run(selectedTaskId(), currentScope());
      setResult(taskResult);
      setMessage(
        isProjectBlockCoverageTask() && coverageExecutionMode() === "queued"
          ? `Coverage work scheduled for ${taskResult.mutatedRows} project(s). Track completion in Task Queue.`
          : `Task completed. Updated ${taskResult.mutatedRows} row(s).`,
      );
      await loadRuns();
    } catch (e: any) {
      setError(e.message ?? "Failed to run maintenance task");
      await loadRuns();
    } finally {
      setLoading(null);
    }
  }

  return (
    <div class="space-y-6">
      <div class="rounded-xl border border-gray-800 bg-gray-900 p-5 shadow-sm">
        <h2 class="text-lg font-semibold text-gray-100">Maintenance</h2>
        <p class="mt-1 text-sm text-gray-400">
          Run reusable cleanup and consistency tasks. Tasks with dry-run support can preview the impacted rows before applying changes, and the same task logic is structured to be schedulable later.
        </p>
      </div>

      <div class="grid gap-6 xl:grid-cols-[24rem_minmax(0,1fr)]">
        <div class="space-y-4 rounded-xl border border-gray-800 bg-gray-900 p-5">
          <div>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Task</label>
            <div class="relative">
              <button
                onClick={() => setTaskDropdownOpen((v) => !v)}
                class="flex w-full items-center justify-between rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 hover:border-gray-600 cursor-pointer"
              >
                <span class="truncate">{selectedTask()?.label ?? "Select task..."}</span>
                <span class="ml-2 shrink-0 text-gray-500">{taskDropdownOpen() ? "▲" : "▼"}</span>
              </button>
              <Show when={taskDropdownOpen()}>
                <>
                  <div class="absolute left-0 top-full z-50 mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
                    <div class="max-h-72 overflow-y-auto">
                      <For each={tasks()}>
                        {(task) => (
                          <button
                            onClick={() => {
                              setSelectedTaskId(task.id);
                              applyTaskDefaults(task.id);
                              setTaskDropdownOpen(false);
                              setResult(null);
                              setMessage("");
                            }}
                            class={`w-full cursor-pointer px-4 py-3 text-left border-b border-gray-800 last:border-b-0 hover:bg-gray-800 transition-colors ${selectedTaskId() === task.id ? "bg-gray-800" : ""}`}
                          >
                            <div class="text-sm font-medium text-gray-100">{task.label}</div>
                            <div class="mt-0.5 text-xs leading-4 text-gray-500 line-clamp-2">{task.description}</div>
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                  <div class="fixed inset-0 z-40" onClick={() => setTaskDropdownOpen(false)} />
                </>
              </Show>
            </div>
            <Show when={selectedTask()}>
              {(task) => <p class="mt-2 text-xs leading-5 text-gray-400">{task().description}</p>}
            </Show>
          </div>

          <Show when={!isQueueHistoryCleanupTask()}><div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            <div>
              <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Organization Scope</label>
              <select
                value={selectedOrganizationId()}
                onInput={(event) => {
                  setSelectedOrganizationId(event.currentTarget.value);
                  if (event.currentTarget.value && !filteredProjects().some((project) => project.id === selectedProjectId())) {
                    setSelectedProjectId("");
                    if (coverageExecutionMode() === "direct") {
                      setCoverageExecutionMode("queued");
                    }
                  }
                }}
                class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
              >
                <option value="">
                  {isProjectBlockCoverageTask() ? "All organizations (system-wide)" : "All organizations"}
                </option>
                <For each={organizations()}>
                  {(org) => <option value={org.id}>{org.name}</option>}
                </For>
              </select>
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Project Scope</label>
              <select
                value={selectedProjectId()}
                onInput={(event) => {
                  setSelectedProjectId(event.currentTarget.value);
                  if (!event.currentTarget.value && coverageExecutionMode() === "direct") {
                    setCoverageExecutionMode("queued");
                  }
                }}
                class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
              >
                <option value="">
                  {isProjectBlockCoverageTask()
                    ? selectedOrganizationId()
                      ? "All eligible projects in selected organization"
                      : "All eligible projects across all organizations"
                    : "All projects"}
                </option>
                <For each={filteredProjects()}>
                  {(project) => <option value={project.id}>{project.name}</option>}
                </For>
              </select>
            </div>
          </div></Show>

          <div>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Preview Row Limit</label>
            <input
              type="number"
              min="1"
              max="500"
              value={previewLimit()}
              onInput={(event) => setPreviewLimit(Math.max(1, Math.min(500, Number(event.currentTarget.value) || 100)))}
              class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
            />
          </div>

          <Show when={isQueueHistoryCleanupTask()}>
            <div class="space-y-4 rounded-lg border border-gray-800 bg-gray-950/60 p-4">
              <div>
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-400">History categories</div>
                <p class="mt-1 text-[11px] leading-4 text-gray-500">Choose one or both operational history streams. At least one remains selected.</p>
                <div class="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                  <For each={[
                    ["logical_executions", "Logical executions", "Scheduler runs that attempted to claim queue work."],
                    ["platform_wakeups", "Platform wake-ups", "Physical VPS ticks and Cloudflare scheduled events."],
                  ] as const}>{([value, label, description]) => <label class="flex items-start gap-2 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200"><input type="checkbox" checked={queueHistoryRecordTypes().includes(value)} onInput={() => toggleQueueHistoryRecordType(value)} class="mt-0.5" /><span><span class="block font-medium">{label}</span><span class="block text-[11px] leading-4 text-gray-500">{description}</span></span></label>}</For>
                </div>
              </div>
              <div>
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-400">Included statuses</div>
                <div class="mt-3 grid grid-cols-2 gap-2">
                  <For each={["completed", "failed", "interrupted", "skipped_overlap"] as const}>{(status) => <label class="flex items-center gap-2 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200"><input type="checkbox" checked={queueHistoryStatuses().includes(status)} onInput={() => toggleQueueHistoryStatus(status)} /><span class="capitalize">{status.replaceAll("_", " ")}</span></label>}</For>
                </div>
              </div>
              <label class="flex items-start gap-2 rounded-lg border border-cyan-900/60 bg-cyan-950/20 px-3 py-2 text-sm text-gray-200"><input type="checkbox" checked={queueHistoryEmptyOnly()} onInput={(event) => setQueueHistoryEmptyOnly(event.currentTarget.checked)} class="mt-0.5" /><span><span class="block font-medium">Only records that claimed no work</span><span class="block text-[11px] leading-4 text-gray-500">Enabled by default. Turning this off allows age/status cleanup of executions that performed work.</span></span></label>
            </div>
          </Show>

          <Show when={isProjectBlockCoverageTask()}>
            <div class="space-y-4 rounded-lg border border-gray-800 bg-gray-950/60 p-4">
              <div>
                <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Execution Mode
                </label>
                <select
                  value={coverageExecutionMode()}
                  onInput={(event) =>
                    setCoverageExecutionMode(
                      event.currentTarget.value as "queued" | "direct",
                    )
                  }
                  class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                >
                  <option value="queued">Queued (recommended)</option>
                  <option value="direct" disabled={!selectedProjectId()}>
                    Direct - selected project only
                  </option>
                </select>
                <p class="mt-1 text-[11px] leading-4 text-gray-500">
                  Queued mode schedules durable batches and wakes the queue immediately.
                  Direct mode keeps this request open and computes the selected project
                  without creating queue jobs.
                </p>
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <For each={[
                  ["Maximum blocks / task", coverageBlocksPerBatch, setCoverageBlocksPerBatch, 1],
                  ["Cell budget / task", coverageCellsPerBatch, setCoverageCellsPerBatch, 100],
                  ["Candidate session safety cap", coverageCandidateSessionsPerBatch, setCoverageCandidateSessionsPerBatch, 1],
                  ["Task time budget (ms)", coverageTaskTimeBudgetMs, setCoverageTaskTimeBudgetMs, 1000],
                ] as const}>
                  {([label, value, setter, minimum]) => (
                    <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {label}
                      <input
                        type="number"
                        min={minimum}
                        value={value()}
                        onInput={(event) => setter(Math.max(minimum, Number(event.currentTarget.value) || minimum))}
                        class="mt-1 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100"
                      />
                    </label>
                  )}
                </For>
              </div>
              <p class="text-[11px] leading-4 text-gray-500">
                Both batch limits apply. Planning starts a new batch when either the
                block count or estimated cell count would exceed its limit. The dry-run
                result reports the effective batch size and the binding limit. The
                candidate-session value is a per-task failure guard; it does not divide
                one block batch into additional session-based tasks.
              </p>
              <div class="rounded-lg border border-gray-800 bg-gray-950 p-3">
                <div class="mb-3">
                  <div class="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Session eligibility
                  </div>
                  <p class="mt-1 text-[11px] leading-4 text-gray-500">
                    Controls which session paths are captured by this coverage generation.
                    Existing fresh path materializations are reused regardless of age.
                  </p>
                </div>
                <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
                  <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Minimum closed-session age
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={coverageMinimumSessionAgeValue()}
                      onInput={(event) =>
                        setCoverageMinimumSessionAgeValue(
                          Math.max(0, Number(event.currentTarget.value) || 0),
                        )
                      }
                      class="mt-1 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100"
                    />
                  </label>
                  <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Unit
                    <select
                      value={coverageMinimumSessionAgeUnit()}
                      onInput={(event) =>
                        setCoverageMinimumSessionAgeUnit(
                          event.currentTarget.value as "minutes" | "hours" | "days",
                        )
                      }
                      class="mt-1 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </label>
                </div>
                <label class="mt-3 flex items-start gap-2 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={coverageIncludeActiveSessions()}
                    onInput={(event) => setCoverageIncludeActiveSessions(event.currentTarget.checked)}
                    class="mt-0.5"
                  />
                  <span>
                    <span class="block font-medium">Include active sessions</span>
                    <span class="block text-[11px] leading-4 text-gray-500">
                      Captures running sessions up to the generation's sample cutoff.
                    </span>
                  </span>
                </label>
              </div>
              <label class="flex items-start gap-2 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200">
                <input
                  type="checkbox"
                  checked={forceCoverageRebuild()}
                  onInput={(event) => setForceCoverageRebuild(event.currentTarget.checked)}
                  class="mt-0.5"
                />
                <span>
                  <span class="block font-medium">Force rebuild</span>
                  <span class="block text-[11px] leading-4 text-gray-500">
                    Rebuild even when the published path and geometry markers are current.
                  </span>
                </span>
              </label>
              <Show when={!selectedProjectId() && Boolean(selectedOrganizationId())}>
                <p class="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200">
                  Organization scope includes every non-archived project with actual blocks.
                </p>
              </Show>
              <Show when={!selectedProjectId() && !selectedOrganizationId()}>
                <p class="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs leading-5 text-amber-100">
                  System-wide scope includes every non-archived project with actual blocks
                  across all organizations. It is queued and processed in durable batches;
                  direct execution is unavailable.
                </p>
              </Show>
            </div>
          </Show>

          <Show when={showsAgeFilters()}>
            <div class="space-y-4 rounded-lg border border-gray-800 bg-gray-950/60 p-4">
              <Show when={showsSourceStatusFilters()}>
                <div>
                  <label class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Included Statuses</label>
                  <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                    <For each={["paused", "disconnected", "completed", "expired"]}>
                      {(status) => (
                        <label class="flex items-center gap-2 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200">
                          <input
                            type="checkbox"
                            checked={selectedStatuses().includes(status)}
                            onInput={() => toggleStatus(status)}
                          />
                          <span class="capitalize">{status}</span>
                        </label>
                      )}
                    </For>
                  </div>
                </div>
              </Show>

              <Show when={isStaleSessionTask()}>
                <div class="space-y-3">
                  <div>
                    <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Source statuses
                    </div>
                    <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                      <For each={["paused", "disconnected"] as const}>
                        {(status) => (
                          <label class="flex items-center gap-2 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={staleSessionSourceStatuses().includes(status)}
                              onInput={() => toggleStaleSessionSourceStatus(status)}
                            />
                            <span class="capitalize">{status}</span>
                          </label>
                        )}
                      </For>
                    </div>
                    <p class="mt-1 text-[11px] leading-4 text-gray-500">
                      Select one or both statuses. At least one source status is required.
                    </p>
                  </div>
                  <div>
                    <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Quick age presets
                    </div>
                    <div class="grid grid-cols-3 gap-2">
                      <For each={[1, 7, 24]}>
                        {(hours) => (
                          <button
                            type="button"
                            onClick={() => {
                              setMinAgeValue(hours);
                              setMinAgeUnit("hours");
                            }}
                            class={`cursor-pointer rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${minAgeUnit() === "hours" && minAgeValue() === hours ? "border-cyan-500 bg-cyan-500/15 text-cyan-100" : "border-gray-700 bg-gray-950 text-gray-300 hover:border-gray-600"}`}
                          >
                            {hours}h
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                  <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Target status
                    <select
                      value={pausedTargetStatus()}
                      onInput={(event) => setPausedTargetStatus(event.currentTarget.value as "completed" | "disconnected" | "expired")}
                      class="mt-1 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100"
                    >
                      <option value="completed">Completed</option>
                      <option value="disconnected">Disconnected</option>
                      <option value="expired">Expired</option>
                    </select>
                  </label>
                  <p class="text-[11px] leading-4 text-gray-500">
                    Sessions already in the target status are skipped. Age uses the session end time, with last activity as a fallback for legacy rows.
                  </p>
                </div>
              </Show>

              <Show when={isEmptySessionArchiveTask()}>
                <div>
                  <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Max Persisted Points</label>
                  <input
                    type="number"
                    min="0"
                    value={maxPoints()}
                    onInput={(event) => setMaxPoints(Math.max(0, Math.floor(Number(event.currentTarget.value) || 0)))}
                    class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  />
                </div>
              </Show>

              <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem] xl:grid-cols-1">
                <div>
                  <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Minimum Age</label>
                  <input
                    type="number"
                    min="1"
                    value={minAgeValue()}
                    onInput={(event) => setMinAgeValue(Math.max(1, Number(event.currentTarget.value) || 1))}
                    class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Age Unit</label>
                  <select
                    value={minAgeUnit()}
                    onInput={(event) => setMinAgeUnit(event.currentTarget.value as "seconds" | "minutes" | "hours" | "days")}
                    class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  >
                    <option value="seconds">Seconds</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              </div>

              <Show when={isSessionPathMaterializationTask()}>
                <div>
                  <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Minimum Persisted Samples</label>
                  <input
                    type="number"
                    min="0"
                    value={minSamples()}
                    onInput={(event) => setMinSamples(Math.max(0, Math.floor(Number(event.currentTarget.value) || 0)))}
                    class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  />
                  <p class="mt-1 text-[11px] text-gray-500">Defaults to 1 so repair targets sessions with at least one persisted point.</p>
                </div>
              </Show>

              <Show when={isSessionPathMaterializationTask() || isStaleSessionTask() || isQueueHistoryCleanupTask()}>
                <div>
                  <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Maximum Updates Per Run</label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={runBatchLimit()}
                    onInput={(event) => setRunBatchLimit(Math.max(1, Math.min(500, Math.floor(Number(event.currentTarget.value) || 100))))}
                    class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  />
                  <p class="mt-1 text-[11px] text-gray-500">
                    Preview reports the full match count. A real run changes at most this many of the oldest matching records.
                  </p>
                </div>
              </Show>
            </div>
          </Show>

          <div class="flex flex-wrap gap-3 pt-2">
            <button
              onClick={() => void runPreview()}
              disabled={loading() !== null}
              class="cursor-pointer rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading() === "preview" ? "Previewing..." : "Dry Run Preview"}
            </button>
            <button
              onClick={() => void runTask()}
              disabled={loading() !== null}
              class="cursor-pointer rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading() === "run" ? "Running..." : "Run Task"}
            </button>
          </div>

          <Show when={error()}>
            <div class="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">{error()}</div>
          </Show>
          <Show when={message()}>
            <div class="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{message()}</div>
          </Show>
        </div>

        <div class="rounded-xl border border-gray-800 bg-gray-900 p-5">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold text-gray-100">Task Result</h3>
              <p class="mt-1 text-xs text-gray-500">Dry-run previews show the rows that would be affected. Run results keep the same preview rows for auditability.</p>
            </div>
            <Show when={result()}>
              {(taskResult) => (
                <div class="flex flex-wrap gap-2 text-xs">
                  <span class="rounded-full bg-gray-800 px-3 py-1 text-gray-300">Matched: {taskResult().matchedRows}</span>
                  <span class="rounded-full bg-gray-800 px-3 py-1 text-gray-300">Mutated: {taskResult().mutatedRows}</span>
                  <span class="rounded-full bg-gray-800 px-3 py-1 text-gray-300">{taskResult().dryRun ? "Dry run" : "Applied"}</span>
                </div>
              )}
            </Show>
          </div>

          <Show when={result()} fallback={<div class="mt-6 rounded-lg border border-dashed border-gray-700 p-8 text-center text-sm text-gray-500">Run a dry-run preview to inspect the rows a maintenance task would affect.</div>}>
            {(taskResult) => (
              <div class="mt-5 space-y-4">
                <div class="grid gap-2 text-xs text-gray-400 sm:grid-cols-3">
                  <div class="rounded-lg bg-gray-950 px-3 py-2">Task: <span class="text-gray-200">{taskResult().taskId}</span></div>
                  <div class="rounded-lg bg-gray-950 px-3 py-2">Executed: <span class="text-gray-200">{new Date(taskResult().executedAt).toLocaleString()}</span></div>
                  <div class="rounded-lg bg-gray-950 px-3 py-2">Scope: <span class="text-gray-200">{taskResult().scope.projectId ?? taskResult().scope.organizationId ?? "Global"}</span></div>
                </div>

                <div class="overflow-x-auto rounded-lg border border-gray-800">
                  <table class="w-full text-sm">
                    <thead class="bg-gray-950 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th class="px-3 py-2 font-semibold">
                          {isProjectBlockCoverageTask() ? "Project ID" : "Session"}
                        </th>
                        <th class="px-3 py-2 font-semibold">Project</th>
                        <th class="px-3 py-2 font-semibold">Organization</th>
                        <For each={taskResult().columns}>
                          {(column) => <th class="px-3 py-2 font-semibold">{column.label}</th>}
                        </For>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={taskResult().rows}>
                        {(row) => (
                          <tr class="border-t border-gray-800">
                            <td class="px-3 py-2 font-mono text-xs text-cyan-300">{row.id}</td>
                            <td class="px-3 py-2 text-gray-300">
                              <div>{row.projectName ?? row.projectId ?? "Unknown project"}</div>
                              <div class="text-[11px] text-gray-500">{row.projectId}</div>
                            </td>
                            <td class="px-3 py-2 text-gray-300">
                              <div>{row.organizationName ?? row.organizationId ?? "Unknown org"}</div>
                              <div class="text-[11px] text-gray-500">{row.organizationId}</div>
                            </td>
                            <For each={taskResult().columns}>
                              {(column) => <td class="px-3 py-2 text-gray-300">{String(row.values[column.key] ?? "—")}</td>}
                            </For>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Show>
        </div>
      </div>

      <div class="rounded-xl border border-gray-800 bg-gray-900 p-5">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 class="text-sm font-semibold text-gray-100">Latest Task Status</h3>
            <p class="mt-1 text-xs text-gray-500">A global overview of the most recent persisted run for each maintenance task.</p>
          </div>
          <div class="text-xs text-gray-500">
            {latestRuns().length} task{latestRuns().length === 1 ? "" : "s"}
          </div>
        </div>

        <div class="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <For each={tasks()}>
            {(task) => {
              const summary = () => latestRuns().find((entry) => entry.taskId === task.id) ?? { taskId: task.id, latestRun: null };
              return (
                <div class="rounded-lg border border-gray-800 bg-gray-950 p-4">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <h4 class="truncate text-sm font-semibold text-gray-100">{task.label}</h4>
                      <p class="mt-1 text-[11px] text-gray-500">{task.id}</p>
                    </div>
                    <Show
                      when={summary().latestRun}
                      fallback={<span class="rounded-full bg-gray-800 px-2.5 py-1 text-xs font-semibold text-gray-300">Never run</span>}
                    >
                      {(latestRun) => (
                        <span class={`rounded-full px-2.5 py-1 text-xs font-semibold ${latestRun().status === "completed" ? "bg-emerald-500/10 text-emerald-200" : "bg-rose-500/10 text-rose-200"}`}>
                          {latestRun().status}
                        </span>
                      )}
                    </Show>
                  </div>

                  <Show when={summary().latestRun}>
                    {(latestRun) => (
                      <div class="mt-4 space-y-2 text-xs text-gray-300">
                        <div>Triggered by: <span class="text-gray-100">{latestRun().triggeredBy ?? "Unknown"}</span></div>
                        <div>Started: <span class="text-gray-100">{new Date(latestRun().startedAt).toLocaleString()}</span></div>
                        <div>Completed: <span class="text-gray-100">{formatOptionalDate(latestRun().completedAt)}</span></div>
                        <div class="flex flex-wrap gap-2 pt-1">
                          <span class="rounded-full bg-gray-900 px-2.5 py-1 text-[11px] text-gray-300">Matched: {latestRun().matchedRows}</span>
                          <span class="rounded-full bg-gray-900 px-2.5 py-1 text-[11px] text-gray-300">Mutated: {latestRun().mutatedRows}</span>
                        </div>
                      </div>
                    )}
                  </Show>
                </div>
              );
            }}
          </For>
        </div>
      </div>

      <div class="rounded-xl border border-gray-800 bg-gray-900 p-5">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 class="text-sm font-semibold text-gray-100">Run History</h3>
            <p class="mt-1 text-xs text-gray-500">Only real maintenance executions are persisted here. Dry-run previews stay transient.</p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <div class="text-xs text-gray-500">
              {runsResponse().total} total run{runsResponse().total === 1 ? "" : "s"}
            </div>
            <button
              onClick={() => void loadRuns()}
              disabled={runsLoading()}
              class="cursor-pointer rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-gray-600 hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {runsLoading() ? "Refreshing..." : "Refresh history"}
            </button>
          </div>
        </div>

        <div class="mt-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,12rem)_minmax(0,8rem)]">
          <div>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Task Type</label>
            <select
              value={historyTaskId()}
              onInput={(event) => updateHistoryFilters({ taskId: event.currentTarget.value })}
              class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
            >
              <option value="">All tasks</option>
              <For each={tasks()}>
                {(task) => <option value={task.id}>{task.label}</option>}
              </For>
            </select>
          </div>

          <div>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Status</label>
            <select
              value={historyStatus()}
              onInput={(event) => updateHistoryFilters({ status: event.currentTarget.value as "" | "completed" | "failed" })}
              class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
            >
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Per Page</label>
            <select
              value={String(historyPerPage())}
              onInput={(event) => updateHistoryFilters({ perPage: Number(event.currentTarget.value) || 25 })}
              class="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>

        <Show when={runs().length > 0} fallback={<div class="mt-5 rounded-lg border border-dashed border-gray-700 p-6 text-center text-sm text-gray-500">No maintenance runs have been recorded yet.</div>}>
          <div class="mt-5 overflow-x-auto rounded-lg border border-gray-800">
            <table class="w-full text-sm">
              <thead class="bg-gray-950 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th class="px-3 py-2 font-semibold">Task</th>
                  <th class="px-3 py-2 font-semibold">Status</th>
                  <th class="px-3 py-2 font-semibold">Triggered By</th>
                  <th class="px-3 py-2 font-semibold">Scope</th>
                  <th class="px-3 py-2 font-semibold">Matched</th>
                  <th class="px-3 py-2 font-semibold">Mutated</th>
                  <th class="px-3 py-2 font-semibold">Started</th>
                  <th class="px-3 py-2 font-semibold">Completed</th>
                </tr>
              </thead>
              <tbody>
                <For each={runs()}>
                  {(run) => (
                    <tr class="border-t border-gray-800 align-top">
                      <td class="px-3 py-2 text-gray-200">
                        <div class="font-medium">{tasks().find((task) => task.id === run.taskId)?.label ?? run.taskId}</div>
                        <div class="text-[11px] text-gray-500">{run.taskId}</div>
                      </td>
                      <td class="px-3 py-2">
                        <span class={`rounded-full px-2.5 py-1 text-xs font-semibold ${run.status === "completed" ? "bg-emerald-500/10 text-emerald-200" : "bg-rose-500/10 text-rose-200"}`}>
                          {run.status}
                        </span>
                      </td>
                      <td class="px-3 py-2 text-gray-300">{run.triggeredBy ?? "Unknown"}</td>
                      <td class="px-3 py-2 text-gray-300">
                        {run.scope.projectId ?? run.scope.organizationId ?? "Global"}
                      </td>
                      <td class="px-3 py-2 text-gray-300">{run.matchedRows}</td>
                      <td class="px-3 py-2 text-gray-300">{run.mutatedRows}</td>
                      <td class="px-3 py-2 text-gray-300">{new Date(run.startedAt).toLocaleString()}</td>
                      <td class="px-3 py-2 text-gray-300">
                        <div>{run.completedAt ? new Date(run.completedAt).toLocaleString() : "—"}</div>
                        <Show when={run.errorText}>
                          <div class="mt-1 text-[11px] text-rose-300">{run.errorText}</div>
                        </Show>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>

          <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div class="text-xs text-gray-500">
              Page {runsResponse().page} of {runsResponse().totalPages}
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button
                onClick={() => goToHistoryPage(Math.max(1, runsResponse().page - 1))}
                disabled={runsLoading() || runsResponse().page <= 1}
                class="cursor-pointer rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-gray-600 hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <For each={visiblePages()}>
                {(pageNumber) => (
                  <button
                    onClick={() => goToHistoryPage(pageNumber)}
                    disabled={runsLoading()}
                    class={`cursor-pointer rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      pageNumber === runsResponse().page
                        ? "bg-cyan-600 text-white"
                        : "border border-gray-700 bg-gray-950 text-gray-200 hover:border-gray-600 hover:bg-gray-900"
                    }`}
                  >
                    {pageNumber}
                  </button>
                )}
              </For>
              <button
                onClick={() => goToHistoryPage(Math.min(runsResponse().totalPages, runsResponse().page + 1))}
                disabled={runsLoading() || runsResponse().page >= runsResponse().totalPages}
                class="cursor-pointer rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-gray-600 hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}
